import { useState, useEffect, useCallback, useRef } from 'react';
import { getClient, getChannelToken } from '@/lib/graphql/client';
import { useAuth } from '@/lib/auth/auth-context';
import { DocumentInput } from '@urql/core';
import { resolveRequestError } from '@/lib/errors/request-error';

// Only patterns that indicate the session is gone (not permission denied).
// commerce kernel returns FORBIDDEN for both expired sessions AND insufficient permissions
// with the same "not currently authorized" message, so we can't use that pattern.
// Permission errors should show as errors on the page, not trigger logout.
const AUTH_ERROR_PATTERNS = [
  'not authenticated',
  'session has expired',
  'must be logged in',
  'token has expired',
  'invalid or expired',
  'invalid token',
  'unauthorized',
];

export function isAuthError(error: unknown): boolean {
  if (!error) return false;
  const err = error as { message?: string; response?: { status?: number }; networkError?: { message?: string } };
  const status = err?.response?.status;
  if (status === 401) return true;
  const messages = [err?.message, err?.networkError?.message].filter(Boolean) as string[];
  return messages.some(msg => {
    const lower = msg.toLowerCase();
    return AUTH_ERROR_PATTERNS.some(pattern => lower.includes(pattern));
  });
}

function dispatchAuthExpired() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth-expired'));
  }
}

interface UseQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

interface UseQueryOptions {
  skip?: boolean;
  /** If true, query will wait for channel to be selected. Default: true */
  requiresChannel?: boolean;
  requestPolicy?: 'cache-first' | 'cache-and-network' | 'network-only' | 'cache-only';
}

interface UseLazyQueryOptions {
  requestPolicy?: 'cache-first' | 'cache-and-network' | 'network-only' | 'cache-only';
}

export function useQuery<T>(
  query: DocumentInput,
  variables?: Record<string, unknown>,
  options?: UseQueryOptions
): UseQueryResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(!options?.skip);
  const [error, setError] = useState<string | null>(null);

  // Check if channel is ready via auth context (triggers re-render when channel changes)
  const { activeChannel, loading: authLoading } = useAuth();
  const requiresChannel = options?.requiresChannel !== false;
  const channelReady = !requiresChannel || (!!activeChannel && !authLoading);

  // Use refs to track query and variables to avoid infinite loops
  const queryRef = useRef<DocumentInput>(query);
  const variablesRef = useRef<Record<string, unknown>>(variables || {});
  const serializedVariables = JSON.stringify(variables || {});
  const prevSerializedRef = useRef<string>('');
  const prevChannelIdRef = useRef<string>('');
  const dataChannelIdRef = useRef<string>('');
  // Request sequence counter to discard stale responses
  const requestIdRef = useRef(0);

  // Update refs when inputs change
  queryRef.current = query;
  variablesRef.current = variables || {};

  const fetchData = useCallback(async (forceRefresh = false) => {
    if (options?.skip || !channelReady) {
      setLoading(!channelReady); // Keep loading true if waiting for channel
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const client = getClient();
      // Use current refs to get latest values
      const result = await client.query(queryRef.current, variablesRef.current, {
        requestPolicy: forceRefresh ? 'network-only' : (options?.requestPolicy ?? 'cache-and-network'),
      }).toPromise();

      // Discard if a newer request was initiated while this one was in flight
      if (currentRequestId !== requestIdRef.current) return;

      if (result.error) {
        if (isAuthError(result.error)) {
          dispatchAuthExpired();
          return;
        }
        setError(resolveRequestError(result.error).message);
      } else {
        dataChannelIdRef.current = requiresChannel
          ? String(activeChannel?.id ?? '')
          : '';
        setData(result.data as T);
      }
    } catch (err) {
      // Discard stale errors
      if (currentRequestId !== requestIdRef.current) return;

      setError(resolveRequestError(err).message);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [options?.skip, channelReady, requiresChannel, activeChannel?.id]); // Depend on channel state too

  // Single effect to handle both initial fetch and variable changes
  useEffect(() => {
    // Skip if told to skip or channel not ready
    if (options?.skip || !channelReady) {
      return;
    }

    // Check if this is initial render or if variables changed
    const isInitialRender = prevSerializedRef.current === '';
    const variablesChanged = prevSerializedRef.current !== serializedVariables;
    const currentChannelId = requiresChannel ? String(activeChannel?.id ?? '') : '';
    const channelChanged = prevChannelIdRef.current !== currentChannelId;

    if (isInitialRender || variablesChanged || channelChanged) {
      prevSerializedRef.current = serializedVariables;
      prevChannelIdRef.current = currentChannelId;
      fetchData(false);
    }
  }, [
    activeChannel?.id,
    channelReady,
    fetchData,
    options?.skip,
    requiresChannel,
    serializedVariables,
  ]);

  // Refetch always bypasses cache
  const refetch = useCallback(() => fetchData(true), [fetchData]);

  const currentChannelId = requiresChannel ? String(activeChannel?.id ?? '') : '';
  const hasTenantMismatch = requiresChannel
    && data !== null
    && dataChannelIdRef.current !== currentChannelId;

  return {
    data: hasTenantMismatch ? null : data,
    loading: loading || hasTenantMismatch,
    error: hasTenantMismatch ? null : error,
    refetch,
  };
}

export function useMutation<T, V extends Record<string, unknown> = Record<string, unknown>>(
  mutation: DocumentInput
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(
    async (variables: V): Promise<{ data: T | null; error: string | null; errorCode: string | null }> => {
      setLoading(true);
      setError(null);
      try {
        const client = getClient();
        const result = await client.mutation(mutation, variables).toPromise();
        if (result.error) {
          const resolved = resolveRequestError(result.error);
          if (isAuthError(result.error)) {
            dispatchAuthExpired();
            return { data: null, error: resolved.message, errorCode: resolved.errorCode };
          }
          setError(resolved.message);
          return { data: null, error: resolved.message, errorCode: resolved.errorCode };
        }
        return { data: result.data as T, error: null, errorCode: null };
      } catch (err) {
        const resolved = resolveRequestError(err);
        setError(resolved.message);
        return { data: null, error: resolved.message, errorCode: resolved.errorCode };
      } finally {
        setLoading(false);
      }
    },
    [mutation]
  );

  return { execute, loading, error };
}

// Lazy query - for queries that need to be triggered manually (like search)
export function useLazyQuery<T, V extends Record<string, unknown> = Record<string, unknown>>(
  query: DocumentInput,
  options?: UseLazyQueryOptions
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(
    async (variables: V): Promise<{ data: T | null; error: string | null; errorCode: string | null }> => {
      setLoading(true);
      setError(null);
      try {
        const client = getClient();
        const result = await client.query(query, variables, {
          requestPolicy: options?.requestPolicy ?? 'cache-first',
        }).toPromise();
        if (result.error) {
          const resolved = resolveRequestError(result.error);
          if (isAuthError(result.error)) {
            dispatchAuthExpired();
            return { data: null, error: resolved.message, errorCode: resolved.errorCode };
          }
          setError(resolved.message);
          return { data: null, error: resolved.message, errorCode: resolved.errorCode };
        }
        return { data: result.data as T, error: null, errorCode: null };
      } catch (err) {
        const resolved = resolveRequestError(err);
        setError(resolved.message);
        return { data: null, error: resolved.message, errorCode: resolved.errorCode };
      } finally {
        setLoading(false);
      }
    },
    [options?.requestPolicy, query]
  );

  return { execute, loading, error };
}
